import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { TierBadge, StatusBadge } from "@/components/TierBadge";
import { api, eur, num, fdate, errMsg } from "@/lib/api";
import { EMPTY, useFilters } from "@/context/FilterContext";
import { toast } from "sonner";

const TIERS = [["bronze", "Bronze"], ["silver", "Silver"], ["gold", "Gold"], ["platinum", "Platinum"]];
const ALL = { ...EMPTY, period: "all" };

export default function Customers() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("all");
  const nav = useNavigate();
  const { filters, setFilters, count } = useFilters();

  useEffect(() => {
    if (count || filters.period !== "all") { setFilters(ALL); toast.info("Filtri azzerati: vedi tutti i clienti", { id: "clienti-filtri" }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPage = (page) => {
    setLoading(true);
    const params = { search, tier: tier === "all" ? "" : tier, status: status === "all" ? "" : status, page };
    return api.get("/customers", { params })
      .then((r) => { setError(""); setData((d) => (page === 0 ? r.data : { ...r.data, items: [...d.items, ...r.data.items] })); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => fetchPage(0), 300);
    return () => clearTimeout(t);
  }, [search, tier, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.items || [];
  return (
    <div>
      <PageHeader title="Clienti" subtitle={data ? `${num(data.total)} clienti · ordinati per spesa` : "Caricamento..."} />
      <Panel>
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="customers-search-input" className="pl-9" placeholder="Cerca per nome, carta, email o telefono" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger className="sm:w-44" data-testid="customers-tier-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutti i livelli</SelectItem>
              {TIERS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sm:w-40" data-testid="customers-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="attivo">Attivi</SelectItem>
              <SelectItem value="inattivo">Inattivi</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {error ? (
          <div data-testid="customers-error" className="m-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">Impossibile caricare i clienti: {error}</div>
        ) : !data ? <Empty text="Caricamento clienti..." /> : rows.length === 0 ? <Empty text="Nessun cliente corrisponde ai filtri." /> : (
          <div className="overflow-x-auto">
            <Table data-testid="customers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead><TableHead>Livello</TableHead><TableHead className="text-right">Punti</TableHead>
                  <TableHead className="text-right">Spesa totale</TableHead><TableHead className="text-right">Acquisti</TableHead>
                  <TableHead>Ultimo acquisto</TableHead><TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className="cursor-pointer" onClick={() => nav(`/clienti/${c.id}`)}>
                    <TableCell><div className="font-medium text-slate-800">{c.name}</div><div className="text-xs text-slate-400">{c.email || c.card || c.phone}</div></TableCell>
                    <TableCell><TierBadge tier={c.tier} /></TableCell>
                    <TableCell className="text-right font-mono text-xs">{num(c.points)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{eur(c.total_spent)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{num(c.tx_count)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fdate(c.last_purchase)}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && !error && (
          <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
            <span data-testid="customers-shown-count">Mostrati {num(rows.length)} di {num(data.total)}</span>
            {rows.length < data.total && <Button size="sm" variant="outline" data-testid="customers-load-more" disabled={loading} onClick={() => fetchPage(data.page + 1)}>{loading ? "Caricamento..." : "Carica altri"}</Button>}
          </div>
        )}
      </Panel>
    </div>
  );
}
